/**
 * Досрочно: калькулятор по тапу на долг в «Долгах».
 *
 * Отвечает на вопрос человека его же цифрами: сколько процентов не придётся
 * платить, когда закроется долг и сколько из ближайшего платежа уйдёт на
 * проценты. Считает до нажатия — ни к чему не обязывает (макеты
 * docs/design/accounts-cards-2026-10-09 и debt-interest-2026-10-15).
 *
 * Проценты — по дням, как у банка, поэтому дата важна: раньше внесёшь —
 * меньше заплатишь, и лист говорит, на сколько. Дата сегодня или раньше —
 * досрочный записывается сразу; в будущем — планируется строкой в
 * «Платежах». Банк, который принимает досрочный только в день платежа,
 * выбора даты не оставляет. «Уменьшит срок / платёж» запоминается: от него
 * зависит платёж дальше.
 *
 * Вместо цели «Закрыть долг» — досрочное в самом долге. Цель копила деньги,
 * которые на деле уходили банку: «накоплено» росло, а остаток долга стоял.
 * Теперь «каждый месяц» добавляет сумму к платежу в «Платежах», а «разово»
 * записывает досрочный платёж; остаток долга уменьшается от этих фактов сам.
 */
import { useState } from 'react';
import {
  ZERO, add, addMonths, formatMoney, kop, monthsText, prepayPlan, sub, withDayClamped, type Money, type PlainDate, type PrepayMode,
} from '@skoka/core';
import { AmountInput } from './amount.js';
import { CardAccount, CardDate } from './card-fields.js';
import { Icon } from './icon.js';
import { SheetWrap } from './sheet.js';
import { Tip } from './tip.js';
import { flash } from './flash.js';
import { dayMonth } from './text.js';
import {
  addLoanPrepayment, formatAmountInput, loanPayDate, parseAmountInput, planLoanPrepayment, setLoanExtra, type AppState,
} from './store.js';
import { payAccounts } from './account-groups.js';

const MONTHS_IN = ['январе', 'феврале', 'марте', 'апреле', 'мае', 'июне',
  'июле', 'августе', 'сентябре', 'октябре', 'ноябре', 'декабре'];
const MONTHS_OF = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
/** «в декабре 2042». */
const inMonth = (d: PlainDate): string => `в ${MONTHS_IN[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}`;
/** «до декабря 2042». */
const untilMonth = (d: PlainDate): string => `до ${MONTHS_OF[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}`;

export function PrepaySheet({ state, loanId, onClose, onSaved }: {
  state: AppState; loanId: string; onClose: () => void; onSaved: () => void;
}) {
  const loan = state.loans.find((l) => l.id === loanId);
  const spendable = payAccounts(state.accounts);
  const [monthly, setMonthly] = useState(true);
  // Что сделал банк в прошлый раз — с того и начинаем: у одного кредита это обычно одно и то же.
  const [mode, setMode] = useState<PrepayMode>(loan?.prepayMode ?? loan?.extraMode ?? 'term');
  const [sum, setSum] = useState(loan?.extraMonthly
    ? formatAmountInput(String(loan.extraMonthly / 100)) : '10 000');
  const [acc, setAcc] = useState(spendable[0]?.id ?? '');
  const today = state.today;
  const next = loan ? loanPayDate(loan, state.obligations, today) : today;
  const dayOnly = loan?.earlyRule === 'payment_day';
  const [date, setDate] = useState<string>(dayOnly ? next : today);
  const [busy, setBusy] = useState(false);
  if (!loan) return null;

  const extra = parseAmountInput(sum);
  const when = (dayOnly ? next : date || today) as PlainDate;
  const future = !monthly && when > today;
  const input = {
    today, principal: loan.principal, accrued: loan.accrued ?? ZERO, ratePct: loan.ratePct,
    payment: loan.payment, nextPay: next, payDay: Number(next.slice(8, 10)),
    ...(loan.schedule ? { schedule: loan.schedule } : {}),
    ...(loan.termMonths ? { termMonths: loan.termMonths } : {}),
    extra, monthly,
  };
  const eff = prepayPlan({ ...input, date: when, mode });
  const gainOf = (r: typeof eff) => sub(r.interestBefore, r.interestAfter);
  const saved = gainOf(eff);
  const alt = mode === 'payment' ? prepayPlan({ ...input, date: when, mode: 'term' }) : null;
  /*
   * Сколько стоит дата. Будущий день — во сколько обойдётся ждать до него;
   * сегодня — на сколько это выгоднее дня платежа. Проценты идут каждый
   * день, поэтому раньше — всегда не хуже.
   */
  const rated = loan.ratePct > 0 && extra > ZERO && !monthly;
  /** Совет — целыми рублями: «на 269,67 ₽» читается как бухгалтерия, а не как ответ. */
  const rubR = (m: Money): string => formatMoney(kop(Math.round(m / 100) * 100));
  const waitCost = rated && future ? sub(gainOf(prepayPlan({ ...input, date: today, mode })), saved) : ZERO;
  const earlyGain = rated && !future && !dayOnly && next > today
    ? sub(saved, gainOf(prepayPlan({ ...input, date: next, mode }))) : ZERO;
  /*
   * «По графику» — дата из договора, если она названа: человек знает её
   * наизусть, и расчётная, разошедшаяся на пару месяцев, выглядела бы ошибкой.
   * «Раньше» считается разницей платежей и отсчитывается от неё.
   */
  const planEnd = loan.endDate ?? eff.endBefore;
  const earlier = eff.monthsBefore !== null && eff.monthsAfter !== null ? eff.monthsBefore - eff.monthsAfter : null;
  const newEnd = planEnd && earlier !== null ? addMonths(planEnd, -earlier) : null;
  /** С какого платежа он станет меньше: досрочный в день платежа меняет уже следующий. */
  const cutFrom = when < next ? next : withDayClamped(addMonths(next, 1), Number(next.slice(8, 10)));

  const rows: Array<{ k: string; v: string; sub?: string; good?: boolean }> = [];
  rows.push({
    k: 'Переплата',
    v: extra > ZERO ? `меньше на ${formatMoney(saved)}` : '—',
    ...(alt && extra > ZERO
      ? { sub: `с «Уменьшит срок» — на ${formatMoney(gainOf(alt))}` }
      : { sub: `из ${formatMoney(eff.interestBefore)} процентов впереди` }),
    good: extra > ZERO,
  });
  if (mode === 'term') {
    rows.push({
      k: 'Закроется',
      v: newEnd && extra > ZERO ? inMonth(newEnd) : planEnd ? inMonth(planEnd) : 'не посчитать',
      ...(newEnd && extra > ZERO && earlier !== null && planEnd
        ? { sub: `на ${monthsText(earlier)} раньше · по графику ${inMonth(planEnd)}` }
        : planEnd ? { sub: 'по графику' } : {}),
    });
  } else {
    rows.push({
      k: monthly ? 'Платёж' : `Платёж с ${dayMonth(cutFrom, today)}`,
      v: formatMoney(extra > ZERO ? eff.paymentAfter : loan.payment),
      ...(extra > ZERO ? {
        sub: `вместо ${formatMoney(loan.payment)}${monthly ? ' — и дальше меньше' : ''} · срок прежний${planEnd ? `, ${untilMonth(planEnd)}` : ''}`,
      } : {}),
    });
  }
  // Сколько из ближайшего платежа уйдёт на проценты — это число видно и в приложении банка.
  if (!monthly && when < next) {
    rows.push({
      k: `Проценты в платеже ${dayMonth(next, today)}`,
      v: formatMoney(eff.nextInterestAfter),
      ...(extra > ZERO ? { sub: `вместо ${formatMoney(eff.nextInterestBefore)}` } : {}),
    });
  }

  const total = add(loan.payment, extra);
  const has = (loan.extraMonthly ?? ZERO) > ZERO;
  const whenTip = loan.ratePct > 0 && !dayOnly
    ? `Проценты начисляются каждый день на остаток долга: чем раньше внести, тем раньше он уменьшится.${
      (future ? waitCost : earlyGain) > ZERO
        ? ` ${formatMoney(extra)} сегодня, а не ${dayMonth(future ? when : next, today)}, — переплата меньше ещё на ${rubR(future ? waitCost : earlyGain)}.`
        : ''} Выберешь будущий день — запланирую платёж и отложу на него деньги.`
    : null;
  const outcome = mode === 'term'
    ? `Платёж прежний, ${formatMoney(loan.payment)}${newEnd && earlier ? `, а долг закроется на ${monthsText(earlier)} раньше — ${inMonth(newEnd)}` : ''}.`
    : `Платёж с ${dayMonth(cutFrom, today)} поменяю на ${formatMoney(eff.paymentAfter)} в долге и в «Платежах»: «Можно тратить» будет считать с ним. Сверь с новым графиком в приложении банка.`;

  return (
    <SheetWrap title={`Досрочно: ${loan.name}`} onClose={onClose} className="rcpt">
      <div className="rcform">
        <div className="segs" role="group" aria-label="Как часто">
          <button className={`seg${!monthly ? ' on' : ''}`} aria-pressed={!monthly} onClick={() => setMonthly(false)}>Разово</button>
          <button className={`seg${monthly ? ' on' : ''}`} aria-pressed={monthly} onClick={() => setMonthly(true)}>Каждый месяц</button>
        </div>
        <label className="rcamountedit">
          <AmountInput value={sum} onChange={setSum} placeholder="0" label="Сколько сверх платежа" className="num" />
          <span aria-hidden="true">₽</span>
        </label>

        <div className="rcamounthead"><span>Что сделает банк</span>
          <Tip>При досрочном банк спрашивает, что уменьшить. «Срок» экономит больше: платёж
            тот же, а долг закрывается раньше. Выбор запомню — от него зависит платёж дальше.</Tip></div>
        <div className="segs" role="group" aria-label="Что сделает банк">
          <button className={`seg${mode === 'term' ? ' on' : ''}`} aria-pressed={mode === 'term'} onClick={() => setMode('term')}>Уменьшит срок</button>
          <button className={`seg${mode === 'payment' ? ' on' : ''}`} aria-pressed={mode === 'payment'} onClick={() => setMode('payment')}>Уменьшит платёж</button>
        </div>

        {eff.monthsBefore === null && (
          <div className="warnline bad">Платёж не покрывает проценты: по графику этот долг не гасится.</div>
        )}
        {!loan.allowsEarly && (
          <div className="warnline bad">По договору досрочно гасить нельзя — это только расчёт.</div>
        )}

        <div className="rccard rcsel">
          {rows.map((r) => (
            <div className="rcselrow" key={r.k}><span className="rclabel">{r.k}</span>
              <span className="rcvalue"><span className="rcvalcol">
                <span className={`rcvaltext${r.good ? ' good' : ''}`}>{r.v}</span>
                {r.sub && <small>{r.sub}</small>}
              </span></span></div>
          ))}
        </div>

        {!monthly && extra > ZERO && (
          <div className="rccard rcsel">
            <CardAccount label="С какого счёта" pickerTitle="Платить со счёта" accounts={spendable}
              value={acc} onChange={setAcc} />
            {dayOnly ? (
              <div className="rcselrow"><span className="rclabel">Когда</span>
                <span className="rcvalue"><span className="rcvalcol">
                  <span className="rcvaltext">{dayMonth(next, today)}</span>
                  <small>в день платежа — раньше банк не примет</small>
                </span></span></div>
            ) : (
              <CardDate label="Когда" value={date} {...(whenTip ? { tip: whenTip } : {})}
                text={date === today ? `сегодня, ${dayMonth(today, today)}` : date ? dayMonth(date, today) : 'сегодня'}
                {...(future && waitCost >= kop(100) ? { sub: `сегодня — выгоднее на ${rubR(waitCost)}` }
                  : !future && loan.ratePct > 0 ? { sub: 'раньше — меньше процентов' } : {})}
                onChange={setDate} />
            )}
          </div>
        )}

        {/* Что произойдёт по кнопке — на виду, до нажатия: блоком, как в разборе чека. */}
        {extra > ZERO && loan.allowsEarly && (
          <div className="rcsplit">
            <div className="rcsplithead">
              <span className="rcsplitico"><Icon name="circle-info" size={13} /></span>
              <div className="rcsplittext">
                <b>Что будет</b>
                <p>{monthly
                  ? `В «Платежах» «${loan.name}» станет ${formatMoney(total)} в месяц: ${formatMoney(loan.payment)} по графику и ${formatMoney(extra)} досрочно. Когда отметите «Оплатил», остаток долга уменьшится сам.${mode === 'payment' ? ' Банк будет уменьшать платёж — после каждого «Оплатил» поставлю новый.' : ''}`
                  : future
                    ? `Запланирую досрочный платёж ${formatMoney(extra)} на ${dayMonth(when, today)}: он появится в «Платежах» отдельной строкой, и «Можно тратить» отложит деньги заранее. Отметишь «Оплатил» — долг уменьшится на ${formatMoney(extra)}.${mode === 'payment' ? ` Платёж после него станет ${formatMoney(eff.paymentAfter)}.` : ''}`
                    : `Запишу досрочный платёж ${formatMoney(extra)} по «${loan.name}» с выбранного счёта — долг станет меньше на ${formatMoney(extra)}. ${outcome}`}</p>
              </div>
            </div>
          </div>
        )}

        {loan.allowsEarly && monthly && (
          <button className="btn pri" disabled={busy || extra <= ZERO}
            onClick={async () => { setBusy(true); await setLoanExtra(loan.id, extra, mode); flash(`Досрочно каждый месяц — ${formatMoney(extra)}`); onSaved(); }}>
            {extra <= ZERO ? 'Впишите сумму'
              : has ? `Досрочно по ${formatMoney(extra)} в месяц` : `Добавить ${formatMoney(extra)} досрочно к платежу`}
          </button>
        )}
        {loan.allowsEarly && monthly && has && (
          <button className="textact" disabled={busy}
            onClick={async () => { setBusy(true); await setLoanExtra(loan.id, ZERO); flash('Досрочное убрано'); onSaved(); }}>
            Убрать досрочное</button>
        )}
        {loan.allowsEarly && !monthly && (
          <button className="btn pri" disabled={busy || extra <= ZERO || !acc || !when}
            onClick={async () => {
              setBusy(true);
              if (future) await planLoanPrepayment({ loanId: loan.id, accountId: acc, amount: extra, date: when, mode });
              else await addLoanPrepayment({ loanId: loan.id, accountId: acc, amount: extra, date: when, mode });
              flash(future ? `Досрочный платёж запланирован — «${loan.name}»` : `Досрочно ${formatMoney(extra)} — «${loan.name}»`);
              onSaved();
            }}>
            {extra <= ZERO ? 'Впишите сумму'
              : future ? `Запланировать ${formatMoney(extra)} на ${dayMonth(when, today)}`
                : `Записать досрочный платёж ${formatMoney(extra)}`}
          </button>
        )}
      </div>
    </SheetWrap>
  );
}
